import { Node, findParentNode } from '@tiptap/core';
import '@ifixit/wiki-components/wiki-grid';
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { css } from '@emotion/css';
import { NodeRange } from '@tiptap/pm/model';
import { liftTarget } from '@tiptap/pm/transform';
import { Selection } from '@tiptap/pm/state';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      grid: {
         removeGridNode: () => ReturnType;
         toggleGrid: (columns: number | null) => ReturnType;
         setGridMode: (columns: number | null) => ReturnType;
         increaseColumns: () => ReturnType;
         decreaseColumns: () => ReturnType;
         nextGridCell: () => ReturnType;
      };
   }
}

export const Grid = Node.create<never, never>({
   name: 'grid',
   content: 'block*',
   group: 'block',
   selectable: true,
   renderHTML({ HTMLAttributes }) {
      return ['wiki-grid', HTMLAttributes, 0];
   },
   addNodeView() {
      return ReactNodeViewRenderer(Component);
   },
   addAttributes() {
      return {
         columns: {
            default: null,
         },
      };
   },
   addKeyboardShortcuts() {
      return {
         Tab: ({ editor }) => editor.chain().focus().nextGridCell().run(),
      };
   },
   addCommands() {
      return {
         toggleGrid:
            (columns: number | null) =>
            ({ chain, editor }) => {
               if (editor.isActive(this.name)) {
                  return chain().focus().removeGridNode().run();
               }
               return chain()
                  .toggleWrap(this.name)
                  .updateAttributes(this.name, { columns: columns })
                  .focus()
                  .run();
            },
         setGridMode:
            (columns: number | null) =>
            ({ chain }) =>
               chain().focus().updateAttributes(this.name, { columns: columns }).run(),
         increaseColumns:
            () =>
            ({ chain, editor }) => {
               const current = editor.getAttributes(this.name).columns;
               return chain()
                  .updateAttributes(this.name, { columns: (current || 0) + 1 })
                  .focus()
                  .run();
            },
         decreaseColumns:
            () =>
            ({ chain, editor }) => {
               const current = editor.getAttributes(this.name).columns;
               if (!current || current <= 1) {
                  return false;
               }
               return chain()
                  .updateAttributes(this.name, { columns: current - 1 })
                  .focus()
                  .run();
            },
         // This is a raw command that is used by the editor commands above.
         removeGridNode:
            () =>
            ({ tr, state, dispatch }) => {
               const gridNodeInfo = findParentNode(node => node.type.name === this.name)(
                  state.selection
               );
               if (!gridNodeInfo) return false;
               const gridNode = gridNodeInfo.node;

               const start = tr.doc.resolve(gridNodeInfo.start);
               const end = tr.doc.resolve(gridNodeInfo.start + gridNode.content.size);
               const range = new NodeRange(start, end, gridNodeInfo.depth);
               const target = liftTarget(range);

               if (target === null) return false;
               if (dispatch) {
                  tr.lift(range, target);
               }
               return true;
            },
         nextGridCell:
            () =>
            ({ tr, state, dispatch }) => {
               const gridNodeInfo = findParentNode(node => node.type.name === this.name)(
                  state.selection
               );
               if (!gridNodeInfo) return false;
               const gridNode = gridNodeInfo.node;
               const currentPos = state.selection.from - gridNodeInfo.start;
               const currentChild = gridNode.childAfter(currentPos);
               const nextIndex = currentChild.index + 1;
               const nextChild = gridNode.maybeChild(nextIndex);
               if (nextChild) {
                  // try to move cursor into it
                  const start = tr.doc.resolve(gridNodeInfo.start);
                  const nextNodePos = start.posAtIndex(nextIndex);
                  const nextNodeResolvedPos = tr.doc.resolve(nextNodePos);
                  if (dispatch) {
                     const near = Selection.near(nextNodeResolvedPos);
                     tr.setSelection(near);
                  }
               } else {
                  // if current node is empty, refuse to add another.
                  const content = currentChild.node.isText
                     ? currentChild.node.nodeSize
                     : currentChild.node.childCount;
                  if (content === 0) {
                     // delete the current (empty) node
                     const offset = currentChild.offset;
                     const deletePos = gridNodeInfo.start + offset;
                     if (dispatch) {
                        tr.delete(deletePos, deletePos + 2);
                     }
                     // move after the node
                     const nextPos = gridNodeInfo.start + gridNode.content.size;
                     const nextResolvedPos = tr.doc.resolve(nextPos);
                     if (dispatch) {
                        const near = Selection.near(nextResolvedPos);
                        tr.setSelection(near);
                     }
                     return true;
                  }
                  // insert new paragraph node
                  const newParagraph = state.schema.nodes.paragraph.create();
                  const insertPos = gridNodeInfo.start + gridNode.content.size;
                  if (dispatch) {
                     tr.insert(insertPos, newParagraph);
                     const start = tr.doc.resolve(insertPos);
                     const near = Selection.near(start);
                     tr.setSelection(near);
                  }
               }
               return true;
            },
      };
   },
});

const Component = (props: { node: { attrs: { columns?: number } } }) => (
   <NodeViewWrapper>
      <GridEditor columns={props.node.attrs.columns}>
         <NodeViewContent />
      </GridEditor>
   </NodeViewWrapper>
);

const GridEditor = ({ columns, children }: { columns?: number | null; children: any }) => {
   /*
    * NOTE: The CSS in this GridEditor component should be similar
    * to the CSS in the `wiki-grid` element definition. This is the
    * editor preview version of the `wiki-grid` live CSS.
    */
   const baseStyles = css`
      outline: 1px solid purple;
      > div {
         display: contents;
         > div {
            display: contents;
            > * {
               margin: 0 !important;
               grid-row-end: span 2;
               word-break: break-all;
               overflow-wrap: break-word;
            }
         }
      }
   `;
   const gridStyles = css`
      ${baseStyles}
      display: grid;
      grid-template-columns: repeat(${columns || 1}, 1fr);
      align-items: center;
      justify-items: center;
      align-content: space-between;
      justify-content: space-between;
      gap: 0.5em;
   `;
   const flexStyles = css`
      ${baseStyles}
      width: 100%;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(100px, max-content));
      justify-content: center;
      align-items: end;
      justify-items: center;
      gap: 10px;
   `;
   return <div className={columns === null ? flexStyles : gridStyles}>{children}</div>;
};
